/**
 * dataIntake — the pure concurrency core of the data-intake hooks. No React
 * imports: the race rules unit-test as plain functions (dataIntake.test.ts).
 * Owns which of several overlapping async load runs may write state.
 *
 * WHY generations, not a closure `cancelled` flag: the old pattern gated
 * `setLoading(false)` on `!cancelled`, so a teardown+re-run while the component
 * stayed mounted left the torn-down run's `finally` skipping the clear — the
 * page stuck loading forever. With tickets the latest run always clears
 * `loading`; only genuinely superseded runs drop.
 */

export type IntakeOutcome<T> =
  | { ok: true; value: T }
  | { ok: false; error: string };

export type IntakeTransition<T> =
  | { action: 'drop' }
  | { action: 'commit'; value: T }
  | { action: 'fail'; error: string };

/** Handle for one load run. Stale the moment a newer run begins (or the
 *  tracker is superseded); staleness is permanent — tickets never revive. */
export interface LoadTicket {
  /** Mid-flight gate: may this run still write state? */
  isStale(): boolean;
  settle<T>(outcome: IntakeOutcome<T>): IntakeTransition<T>;
}

/** One logical load slot: a monotonic generation counter issuing tickets. */
export interface LoadGenerations {
  /** Start a run (supersedes all earlier tickets) and return its ticket. */
  begin(): LoadTicket;
  /** Invalidate all outstanding tickets without starting a run. */
  supersede(): void;
}

export function createLoadGenerations(): LoadGenerations {
  let current = 0;
  return {
    begin(): LoadTicket {
      const gen = ++current;
      return {
        isStale: () => current !== gen,
        settle<T>(outcome: IntakeOutcome<T>): IntakeTransition<T> {
          if (current !== gen) return { action: 'drop' };
          return outcome.ok
            ? { action: 'commit', value: outcome.value }
            : { action: 'fail', error: outcome.error };
        },
      };
    },
    supersede(): void {
      current++;
    },
  };
}
